import React, {useEffect, useState} from 'react';
import {useZooPassAppContext} from "../../Context/ZooPassAppContext";
import {IZooPassAnswersContext, useZooPassAppAnswersContext, ZooPassAppAnswersContext} from "../../Context/ZooPassAnswersContext";
import {StepTitleContent} from "../PanelContents/StepTitleContent";
import {HeaderAnswerComponent} from "../PanelContents/HeaderAnswer";
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import ZooPassLocalStateUtility from "../../Utilities/ZooPassLocalStateUtility";
import ZooPassAnswerAvailabilityUtility from "../../Utilities/ZooPassAnswerAvailabilityUtility";

interface Step4State
{
    radio: null | 0 | 1 | 2 | 3 | 4;
    dropdown: null | 4 | 5 | 6 | 7 | 8 | 9;
    calculation: null | Step4Calculation;
    action: "change" | "advance" | "ready";
}

const Step4StateDefault : Step4State = {
    radio: 0,
    dropdown: 4,
    calculation: null,
    action: "ready",
};

export interface Step4Calculation
{
    radio: null | 0 | 1 | 2 | 3 | 4;
    dropdown: null | 4 | 5 | 6 | 7 | 8 | 9;
    final: null | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
}

export const Step4CalculationDefault : Step4Calculation = {
    radio: null,
    dropdown: null,
    final: null
}

export const Step4Panel: React.FC = () => {

    let {
        dictionary,
        headerAnswers,
        textTitles,
        loading
    } = useZooPassAppContext();

    let {
        answersData,
        answersLoading,
        updateContext
    } = useZooPassAppAnswersContext();

    const [state4, setState4] = useState<Step4State>(Step4StateDefault);
    
    useEffect(() => {
        if(state4.action == "ready")
        {
            setState4(Step4StateDefault);
        }
    }, [answersData?.step])
    
    useEffect(() => {
        if(state4.action == "advance"){
            setState4({
                radio: state4.radio,
                dropdown: state4.dropdown,
                calculation: getCalculations(state4.radio, state4.dropdown || 4),
                action: "ready",
            });
        }else if(state4.action == "ready" && state4.calculation != null){
            doAdvance(state4.calculation);
        }
    }, [state4])
    
    ////////////////////////////////////////////////////////////////////////////
    // Main Panels
    ////////////////////////////////////////////////////////////////////////////
    
    const handleRadioClick = (radio: null | 0 | 1 | 2 | 3 | 4) => {
        setState4({
            radio: radio,
            dropdown: state4.dropdown,
            calculation: null,
            action: radio == 4 ? "change" : "advance",
        });
    };

    const handleRadioKeyPress = (event : any, radio: null | 0 | 1 | 2 | 3 | 4) => {
        if(!event.altKey && !event.metaKey && !event.shiftKey && event.key === 'Enter') {
            event.preventDefault();
            setState4({
                radio: radio,
                dropdown: state4.dropdown,
                calculation: null,
                action: radio == 4 ? "change" : "advance",
            });
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    // Dropdown
    ////////////////////////////////////////////////////////////////////////////
    
    const handleDropdownChange = (event) => {
        const value = Number(event.target.value);
        setState4({
            radio: state4.radio,
            dropdown: value as null | 4 | 5 | 6 | 7 | 8 | 9,
            calculation: null,
            action: "change",
        });
    };

    const handleContinueButtonClick = () => {
        setState4({
            radio: state4.radio,
            dropdown: state4.dropdown,
            calculation: null,
            action: "advance",
        });
    };

    const handleContinueButtonKeypress = (event) => {
        if(!event.altKey && !event.metaKey && !event.shiftKey && event.key === 'Enter') {
            event.preventDefault();
            setState4({
                radio: state4.radio,
                dropdown: state4.dropdown,
                calculation: null,
                action: "advance",
            });
        }
    };
    
    function doAdvance(calc : Step4Calculation){
        if(answersData != null){
            answersData.step4Value = calc.radio;
            answersData.step4DropdownValue = calc.dropdown;
            answersData.step4FinalValue = calc.final;
            const result = ZooPassAnswerAvailabilityUtility.CalculateGuestOptionAvailability(answersData);
            if(result.forced5 !== null) answersData.step5Value = result.forced5;
            answersData.previousStep = answersData.step;
            answersData.step = result.next;
            ZooPassLocalStateUtility.SetLocalState(answersData);
            updateContext({type: "setAnswers", value: answersData});
        }
    }
    
    function getCalculations(rval : null | 0 | 1 | 2 | 3 | 4, dval : 4 | 5 | 6 | 7 | 8 | 9) : Step4Calculation {
        if (rval == null) {
            return Step4CalculationDefault;
        }
        if (rval < 4) {
            return {
                radio: rval,
                dropdown: dval || 4,
                final: rval
            };
        }
        if (rval == 4) {
            return {
                radio: rval,
                dropdown: dval || 4,
                final: dval || 4
            }
        }
        return Step4CalculationDefault;
    }

    return !answersLoading && !loading &&
        textTitles !== null &&
        answersData !== null && answersData.step == 4 && (
            <div className="container d-block">
                <StepTitleContent textTitle={textTitles.get("ZooPass:PageContent:04")}/>
                <div className="row g-4 g-lg-3 g-xl-2 mx-auto row-cols-1 row-cols-sm-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-5 justify-content-center">
                    <HeaderAnswerComponent headerAnswerData={headerAnswers.get("ZooPass:PageAnswer:04:0")}
                                           nameValue="step4"
                                           onKeyPressEnter={(event) => handleRadioKeyPress(event, 0)}
                                           onClick={() => handleRadioClick(0)} key="radio0"/>
                    <HeaderAnswerComponent headerAnswerData={headerAnswers.get("ZooPass:PageAnswer:04:1")}
                                           nameValue="step4"
                                           onKeyPressEnter={(event) => handleRadioKeyPress(event, 1)}
                                           onClick={() => handleRadioClick(1)} key="radio1"/>
                    <HeaderAnswerComponent headerAnswerData={headerAnswers.get("ZooPass:PageAnswer:04:2")}
                                           nameValue="step4"
                                           onKeyPressEnter={(event) => handleRadioKeyPress(event, 2)}
                                           onClick={() => handleRadioClick(2)} key="radio2"/>
                    <HeaderAnswerComponent headerAnswerData={headerAnswers.get("ZooPass:PageAnswer:04:3")}
                                           nameValue="step4"
                                           onKeyPressEnter={(event) => handleRadioKeyPress(event, 3)}
                                           onClick={() => handleRadioClick(3)} key="radio3"/>
                    <HeaderAnswerComponent headerAnswerData={headerAnswers.get("ZooPass:PageAnswer:04:4")}
                                           nameValue="step4"
                                           onKeyPressEnter={(event) => handleRadioKeyPress(event, 4)}
                                           onClick={() => handleRadioClick(4)} key="radio4"/>
                </div>
                {state4.radio == 4 && <div className="my-3 d-block">
                    <h3 className="h3 w-100 text-center">{dictionary.get("ZooPass:Common:4Children:Title")}</h3>
                    <div className="d-flex zoopass--center">
                        <Form.Select value={state4.dropdown || 4} onSelect={handleDropdownChange} onChange={handleDropdownChange} aria-label={dictionary.get("ZooPass:Common:4Children:Title")}>
                            <option value="4">4</option>
                            <option value="5">5</option>
                            <option value="6">6</option>
                            <option value="7">7</option>
                            <option value="8">8</option>
                            <option value="9">9</option>
                        </Form.Select>
                        <Button className="ms-3 zoopass--step4--continue-button" onKeyPressCapture={handleContinueButtonKeypress} onClick={handleContinueButtonClick} 
                                variant="secondary">{dictionary.get("ZooPass:Common:4Children:Cta")}</Button>
                    </div>
                </div>}
            </div>
        );
};

